// Runtime-safe (no npm or node: imports): consumed by Next, Vite and — via relative
// path with the `.ts` extension — by Deno edge functions.

/** The only list of enabled locales. The `profiles.locale` CHECK constraint is asserted equal to it in tests. */
export const SUPPORTED_LOCALES = ["en", "sv"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** English is the authored source of truth; every other locale is translated from it. */
export const SOURCE_LOCALE: Locale = "en";

export const DEFAULT_LOCALE: Locale = SOURCE_LOCALE;

/**
 * The language of the product's market. A browser that lists it anywhere in its languages gets it,
 * even when another supported language is ranked above it: a Swedish speaker whose browser was set
 * up in English (ChromeOS keeps its website languages from setup, e.g. `en-US,en;q=0.9,sv;q=0.8`)
 * otherwise got English, and Chrome then machine-translated the page — which rewrites React's text
 * nodes and crashed the bank sheet (FINANCIALHARMONY-CLIENT-N). An explicit choice (profile/cookie)
 * still wins over this.
 */
export const MARKET_LOCALE: Locale = "sv";

/** Each language written in itself — never translated, so a user can always find their own. */
export const LOCALE_NATIVE_NAMES: Record<Locale, string> = {
  en: "English",
  sv: "Svenska",
};

/**
 * The `Intl` locale each language formats dates and times with: Swedish conventions (the product's
 * market — 24-hour time, "2026-10-14") with the language's own words ("14 Oct 2026" vs
 * "14 okt. 2026"). A `Record<Locale, …>` so a new language cannot be added without choosing one.
 */
export const FORMATTING_LOCALES: Record<Locale, string> = {
  en: "en-SE",
  sv: "sv-SE",
};

export const LOCALE_COOKIE_NAME = "fh_locale";

export const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}
